import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link, useLocation } from "react-router-dom";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, BookOpen, FileText, ListChecks, Tags, Upload, Brain,
  Link as LinkIcon, Users, Tag, Shield, MessageSquare, BarChart3, CheckSquare,
  CreditCard, Settings, ChevronDown, ChevronRight, Database, Sparkles, LineChart,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Item = { key: string; label: string; icon: typeof LayoutDashboard };

interface Section {
  key: string;
  label: string;
  icon: typeof LayoutDashboard;
  items: Item[]; // first item acts as the section landing
}

const sections: Section[] = [
  {
    key: "overview",
    label: "Overview",
    icon: LayoutDashboard,
    items: [{ key: "overview", label: "Overview", icon: LayoutDashboard }],
  },
  {
    key: "content",
    label: "Content",
    icon: Database,
    items: [
      { key: "mcqs", label: "MCQs", icon: ListChecks },
      { key: "topics", label: "Topics", icon: BookOpen },
      { key: "notes", label: "Notes", icon: FileText },
      { key: "labels", label: "Bulk Labels", icon: Tags },
      { key: "import", label: "Import", icon: Upload },
      { key: "linking", label: "Link MCQs/Notes", icon: LinkIcon },
    ],
  },
  {
    key: "ai-review",
    label: "AI & Review",
    icon: Sparkles,
    items: [
      { key: "ai", label: "AI Tools", icon: Brain },
      { key: "review", label: "Review Queue", icon: CheckSquare },
    ],
  },
  {
    key: "users-billing",
    label: "Users & Billing",
    icon: Users,
    items: [
      { key: "users", label: "Users", icon: Users },
      { key: "billing", label: "Subscription & Billing", icon: CreditCard },
      { key: "exam-labels", label: "Exams & Pricing", icon: Tag },
    ],
  },
  {
    key: "insights",
    label: "Insights",
    icon: LineChart,
    items: [
      { key: "analytics", label: "Analytics & Reports", icon: BarChart3 },
      { key: "feedback", label: "Feedback & Support", icon: MessageSquare },
      { key: "audit", label: "Audit Logs", icon: Shield },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    icon: Settings,
    items: [{ key: "settings", label: "Settings", icon: Settings }],
  },
];

export default function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const activeTab = location.pathname.split("/")[2] || "overview";

  // Auto-expand the section containing the active route
  const initialOpen = sections.find((s) => s.items.some((i) => i.key === activeTab))?.key;
  const [openSection, setOpenSection] = useState<string | undefined>(initialOpen ?? "overview");

  const { data: pendingFeedbackCount = 0 } = useQuery({
    queryKey: ["admin-pending-feedback-badge"],
    queryFn: async () => {
      const { count } = await supabase
        .from("mcq_user_feedback")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");
      return count || 0;
    },
    refetchInterval: 30000,
  });

  return (
    <Sidebar collapsible="icon" className="w-60 pt-12 md:pt-16">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Back to App">
                  <Link to="/dashboard" className="w-full">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {!collapsed && <span className="truncate font-medium">Back to App</span>}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Admin
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sections.map((section) => {
                const SectionIcon = section.icon;
                const sectionActive = section.items.some((i) => i.key === activeTab);
                const isOpen = openSection === section.key;
                const isSimple = section.items.length === 1;

                if (isSimple) {
                  const only = section.items[0];
                  return (
                    <SidebarMenuItem key={section.key}>
                      <SidebarMenuButton asChild isActive={sectionActive}>
                        <Link to={`/admin/${only.key}`} className="w-full">
                          <SectionIcon className="mr-2 h-4 w-4" />
                          {!collapsed && <span className="truncate">{section.label}</span>}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <div key={section.key}>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={sectionActive}
                        onClick={() => setOpenSection(isOpen ? undefined : section.key)}
                      >
                        <SectionIcon className="mr-2 h-4 w-4" />
                        {!collapsed && (
                          <span className="flex items-center justify-between flex-1">
                            <span className="truncate">{section.label}</span>
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </span>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {isOpen && !collapsed && (
                      <div className="ml-3 border-l pl-2 mt-1 mb-1 space-y-0.5">
                        {section.items.map(({ key, label, icon: Icon }) => {
                          const isActive = key === activeTab;
                          return (
                            <SidebarMenuItem key={key}>
                              <SidebarMenuButton asChild isActive={isActive} className="h-8">
                                <Link to={`/admin/${key}`} className="w-full">
                                  <Icon className={cn("mr-2 h-4 w-4", isActive && "text-primary")} />
                                  <span className="flex items-center justify-between flex-1">
                                    <span className="truncate text-sm">{label}</span>
                                    {key === "feedback" && pendingFeedbackCount > 0 && (
                                      <Badge variant="destructive" className="ml-auto h-5 px-1.5 text-xs">
                                        {pendingFeedbackCount}
                                      </Badge>
                                    )}
                                  </span>
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
